import { ref, inject } from "vue";

const facebookCover = "/images/canva-cta/facebook_cover.png";
const flyer = "/images/canva-cta/flyer.png";
const image = "/images/canva-cta/image.png";
const instagramPost = "/images/canva-cta/instagram_post.png";
const instagramStory = "/images/canva-cta/instagram_story.png";
const presentation = "/images/canva-cta/presentation.png";
const socialMediaPost = "/images/canva-cta/social_media_post.png";
const youtubeThumbnail = "/images/canva-cta/youtube_thumbnail.png";
const tshirt = "/images/canva-cta/tshirt.png";

export function useCanvaDocuments(foreground, width, height, I18n, flavor) {
  const selectedDocument = ref(null);

  const selectDocument = (id) => {
    selectedDocument.value = id;
  };

  const isSelected = (id) => {
    return selectedDocument.value === id;
  };

  const defaultBackgroundPlacement = {
    top: 0,
    left: 0,
    width: "100%",
    height: "auto",
    rotation: 0,
  };

  const defaultForegroundPlacement = {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "auto",
    height: "auto",
    rotation: 0,
  };

  const prodDoctypes = {
    image: null,
    youtubeThumbnail: "TACQ-mQ0NXU",
    instagramPost: "TAEby5mLXEk",
    instagramStory: "TACUOfRHN_8",
    facebookCover: "TAEB2BZnMpk",
    flyer: "TACixUyoqp0",
    tShirt: "TADIXiofzu0",
    presentation: "TACQ-gtv2Yk",
    socialMediaPost: "TADyKgZrACU",
  };

  const devDoctypes = {
    image: null,
    youtubeThumbnail: "TACQ-mQ0NXU",
    instagramPost: "TAEkz90JkFA",
    instagramStory: "TADh8E4QhiQ",
    facebookCover: "TAEfjsc3y2I",
    flyer: "TACiRRRQ1rQ",
    tShirt: "TADNsHfQjZc",
    presentation: "TACQ-gtv2Yk",
    socialMediaPost: "TAEkzyEODhU",
  }

  const doctypes = (flavor === "production") ? prodDoctypes : devDoctypes;

  const documents = [
    {
      meta: {
        id: doctypes.image,
        name: I18n.t("canva_cta.documents.image"),
        width: width,
        height: height,
        unit: "px",
      },
      layers: [
        {
          src: image,
          alt: I18n.t("canva_cta.documents.image"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            maxWidth: "115px",
            maxHeight: "66px",
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.youtubeThumbnail,
        name: I18n.t("canva_cta.documents.youtube_thumbnail"),
        width: 1280,
        height: 720,
        unit: "px",
      },
      layers: [
        {
          src: youtubeThumbnail,
          alt: I18n.t("canva_cta.documents.youtube_thumbnail"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            maxWidth: "60px",
            maxHeight: "40px",
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.instagramPost,
        name: I18n.t("canva_cta.documents.instagram_post"),
        width: 1080,
        height: 1350,
        unit: "px",
      },
      layers: [
        {
          src: instagramPost,
          alt: I18n.t("canva_cta.documents.instagram_post"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            top: "50%",
            left: "52%",
            rotation: -11.7208257, // uff, taken directly from canva.com
            maxWidth: "50px",
            maxHeight: "50px",
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.instagramStory,
        name: I18n.t("canva_cta.documents.instagram_story"),
        width: 1080,
        height: 1920,
        unit: "px",
      },
      layers: [
        {
          src: instagramStory,
          alt: I18n.t("canva_cta.documents.instagram_story"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            top: "40%",
            rotation: 0,
            maxWidth: "40px",
            maxHeight: "33px",
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.presentation,
        name: I18n.t("canva_cta.documents.presentation"),
        width: 1920,
        height: 1080,
        unit: "px",
      },
      layers: [
        {
          src: presentation,
          alt: I18n.t("canva_cta.documents.presentation"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            maxWidth: "115px",
            maxHeight: "66px",
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.socialMediaPost,
        name: I18n.t("canva_cta.documents.social_media_post"),
        width: 1080,
        height: 1080,
        unit: "px",
      },
      layers: [
        {
          src: socialMediaPost,
          alt: I18n.t("canva_cta.documents.social_media_post"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            top: "50%",
            left: "35%",
            maxWidth: "50px",
            maxHeight: "50px",
            rotation: 16,
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.facebookCover,
        name: I18n.t("canva_cta.documents.facebook_cover"),
        width: 1640,
        height: 924,
        unit: "px",
      },
      layers: [
        {
          src: facebookCover,
          alt: I18n.t("canva_cta.documents.facebook_cover"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            top: "50%",
            maxWidth: "110px",
            maxHeight: "45px",
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.flyer,
        name: I18n.t("canva_cta.documents.flyer"),
        width: 210,
        height: 298,
        unit: "mm",
      },
      layers: [
        {
          src: flyer,
          alt: I18n.t("canva_cta.documents.flyer"),
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: "Image with removed background",
          placement: {
            ...defaultForegroundPlacement,
            top: "45%",
            left: "53%",
            rotation: -5,
            maxWidth: "55px",
            maxHeight: "70px",
          },
        },
      ],
    },
    {
      meta: {
        id: doctypes.tShirt,
        name: I18n.t("canva_cta.documents.t_shirt"),
        width: 1920,
        height: 1080,
        unit: "px",
      },
      layers: [
        {
          src: tshirt,
          alt: "T-Shirt",
          placement: defaultBackgroundPlacement,
        },
        {
          src: foreground,
          alt: I18n.t("canva_cta.documents.t_shirt"),
          placement: {
            ...defaultForegroundPlacement,
            top: "65%",
            left: "44%",
            rotation: 5,
            maxWidth: "60px",
            maxHeight: "60px",
          },
        },
      ],
    },
  ];

  return {
    documents,
    selectedDocument,
    selectDocument,
    isSelected,
  };
}
